import React from 'react'
import type { Contact } from 'lib/types'
import { Link } from 'react-router-dom'
import { Table, Row, Cell } from 'components/Table'
import tableStyles from 'components/Table/styles'
import ticketFlag from 'styles/img/ticket.png'
import formatters from 'lib/formatters'
import { getStatusClassName } from '../../routes/Protected/Contacts/components/Search/SearchResult/utils'
import searchResultStyles from '../../routes/Protected/Contacts/components/Search/SearchResult/SearchResult.css'
import styles from './styles'

const ContactRoleRef = (contact: Contact) => {
  let dataRows = []
  const { grantor_for_gpt, co_grantor_for_gpt } = contact
  const previousRoles = contact.previous_roles || []
  const roles = contact.roles || []

  const renderAccountCell = (number, status) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Link to={`/accounts/${number}`} className={tableStyles.gpt} style={{ minWidth: '60px', textAlign: 'center' }}>{number}</Link>
      {status && (
        <span
          className={`${searchResultStyles.statusBadge} ${getStatusClassName(status, searchResultStyles)}`}
          style={{ fontSize: '10px' }}
        >
          {formatters.legibleAccountStatus(status)}
        </span>
      )}
    </span>
  )

  contact.able_for_gpt.map((account, idx) => {
    const {
      number,
      status,
      beneficiary: { name, id },
    } = account
    const nameLink = <Link to={`/beneficiaries/show/${id}`}>{name}</Link>
    dataRows.push(
      <Row key={`able${idx}`}>
        <Cell value="Able" />
        <Cell value={renderAccountCell(number, status)} />
        <Cell value={nameLink} />
        <Cell value={<span />} />
      </Row>,
    )
  })

  contact.attorney_for_gpt.map((account, idx) => {
    const {
      number,
      status,
      beneficiary: { name, id },
    } = account
    const nameLink = <Link to={`/beneficiaries/show/${id}`}>{name}</Link>
    dataRows.push(
      <Row key={`a${idx}`}>
        <Cell value="Attorney" />
        <Cell value={renderAccountCell(number, status)} />
        <Cell value={nameLink} />
        <Cell value={<span />} />
      </Row>,
    )
  })

  contact.beneficiary_of_gpt.map(({ number, status }, idx) => {
    const nameLink = (
      <Link to={`/beneficiaries/show/${contact.id}`}>{contact.name}</Link>
    )
    const { has_open_ticket: hasOpenTicket } = contact
    const openTickets = hasOpenTicket ? (
      <Link to={`/accounts/${number}`}>
        <div className={`${styles.ticketFlagSmall}`}>
          <img src={ticketFlag} />
        </div>
      </Link>
    ) : (
      <span />
    )
    dataRows.push(
      <Row highlight={hasOpenTicket} key={`b${idx}${number}`}>
        <Cell value="Beneficiary" />
        <Cell value={renderAccountCell(number, status)} />
        <Cell value={nameLink} />
        <Cell value={openTickets} />
      </Row>,
    )
  })

  contact.consult_for_gpt.map((account, idx) => {
    const {
      number,
      status,
      beneficiary: { name, id },
    } = account
    const nameLink = <Link to={`/beneficiaries/show/${id}`}>{name}</Link>
    dataRows.push(
      <Row key={`c${idx}`}>
        <Cell value="Consult" />
        <Cell value={renderAccountCell(number, status)} />
        <Cell value={nameLink} />
        <Cell value={<span />} />
      </Row>,
    )
  })

  grantor_for_gpt.concat(co_grantor_for_gpt).map((account, idx) => {
    const {
      number,
      status,
      beneficiary: { name, id },
    } = account
    const nameLink = <Link to={`/beneficiaries/show/${id}`}>{name}</Link>
    dataRows.push(
      <Row key={`g${idx}`}>
        <Cell value="Grantor" />
        <Cell value={renderAccountCell(number, status)} />
        <Cell value={nameLink} />
        <Cell value={<span />} />
      </Row>,
    )
  })

  contact.referrer_for_gpt.map((account, idx) => {
    const {
      number,
      status,
      beneficiary: { name, id },
    } = account
    const nameLink = <Link to={`/beneficiaries/show/${id}`}>{name}</Link>
    dataRows.push(
      <Row key={`referrer${idx}`}>
        <Cell value="Referrer" />
        <Cell value={renderAccountCell(number, status)} />
        <Cell value={nameLink} />
        <Cell value={<span />} />
      </Row>,
    )
  })

  contact.representative_for_gpt.map((account, idx) => {
    const {
      number,
      status,
      beneficiary: { name, id },
    } = account
    const nameLink = <Link to={`/beneficiaries/show/${id}`}>{name}</Link>
    dataRows.push(
      <Row key={`representative${idx}`}>
        <Cell value="Representative" />
        <Cell value={renderAccountCell(number, status)} />
        <Cell value={nameLink} />
        <Cell value={<span />} />
      </Row>,
    )
  })

  roles.map((account, idx) => {
    const {
      number,
      status,
      beneficiary: { name, id },
      role,
    } = account
    const nameLink = <Link to={`/beneficiaries/show/${id}`}>{name}</Link>
    dataRows.push(
      <Row key={`role${idx}`}>
        <Cell value={formatters.unsnake(role)} />
        <Cell value={renderAccountCell(number, status)} />
        <Cell value={nameLink} />
        <Cell value={<span />} />
      </Row>,
    )
  })

  previousRoles.map((account, idx) => {
    const {
      number,
      status,
      beneficiary: { name, id },
      role,
    } = account
    const nameLink = <Link to={`/beneficiaries/show/${id}`}>{name}</Link>
    dataRows.push(
      <Row key={`previous${idx}`}>
        <Cell value={`PREVIOUS ${role}`} />
        <Cell value={renderAccountCell(number, status)} />
        <Cell value={nameLink} />
        <Cell value={<span />} />
      </Row>,
    )
  })

  let noResults = dataRows.length ? null : 'No roles held.'

  return (
    <Table
      headers={['Role', 'Account', 'Beneficiary', '']}
      tableSize="small"
      tableType="read"
      title="Contact Roles"
      noResults={noResults}
    >
      {dataRows}
    </Table>
  )
}

export default ContactRoleRef